import React, { useContext, useEffect, useState } from 'react';
import { Row, notification, Button, Modal } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import CreateUpdateAddonForm from '../../components/CreateUpdateAddonForm/CreateUpdateAddonForm';
import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';

function AddonDetail() {
  const { id } = useParams();
  const addonId = id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setHeaderComponent } = useContext(LayoutContext);

  const { doFetch: fetchLanguages } = useFetch();
  const { doFetch: fetchAddon } = useFetch();
  const { doFetch: updateAddonFetch } = useFetch();
  const { doFetch: uploadImageFetch } = useFetch();
  const { doFetch: deleteAddonFetch } = useFetch();

  const [languageList, setLanguageList] = useState([]);
  const [selectedAddon, setSelectedAddon] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const [languagesData, addonRes] = await Promise.all([
          new Promise((resolve, reject) => {
            fetchLanguages({ url: URLS.languages, onSuccess: resolve, onError: reject });
          }),
          new Promise((resolve, reject) => {
            fetchAddon({
              url: URLS.addonDetails(addonId),
              onSuccess: resolve,
              onError: reject,
            });
          }),
        ]);

        setLanguageList(languagesData);
        const addon = addonRes.addon;
        if (addon) {
          const nameFields = {};
          addon.addon_translations?.forEach(tr => {
            nameFields[`name.${tr.language_id}`] = tr.name_text;
          });
          setSelectedAddon({
            id: addon.id,
            ...nameFields,
            price: addon.price,
            priceWholesale: addon.price_wholesale,
            currency: addon.currency,
            isInStock: addon.is_in_stock,
            image: addon.image_url,
          });
        }
        setHeaderComponent(<b>{`Addon #${addonId}`}</b>);
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
    return () => setHeaderComponent(null);
  }, [addonId, fetchAddon, fetchLanguages]);

  const uploadImage = async (_, formValues) => {
    if (!formValues?.image?.file?.originFileObj) {
      notification.success({ placement: 'bottomRight', message: 'Addon updated.', duration: 3 });
      queryClient.invalidateQueries({ queryKey: ['addons'] });
      navigate('/addons');
      return;
    }
    await uploadImageFetch({
      method: 'FILE_POST',
      url: URLS.uploadAddonImage({ addonId }),
      params: { image: formValues.image.file.originFileObj },
      showSuccessNotification: true,
      successMessage: 'Addon updated.',
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['addons'] });
        navigate('/addons');
      },
    });
  };

  const onSubmit = async formValues => {
    const translationsObject = languageList.reduce((acc, language) => {
      acc[language.id] = formValues[`name.${language.id}`];
      return acc;
    }, {});

    await updateAddonFetch({
      url: URLS.updateAddon({ addonId }),
      method: 'PUT',
      params: {
        ...translationsObject,
        price: formValues.price,
        price_wholesale: formValues.priceWholesale,
        currency: formValues.currency,
        is_in_stock: formValues.isInStock,
      },
      onSuccess: data => uploadImage(data, formValues),
    });
  };

  const handleDelete = () => {
    deleteAddonFetch({
      url: URLS.deleteAddon({ addonId }),
      method: 'DELETE',
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['addons'] });
        navigate('/addons');
      },
    });
  };

  if (isLoading || !selectedAddon) {
    return (
      <Row>
        <div style={{ textAlign: 'center', padding: '50px' }}>Loading addon...</div>
      </Row>
    );
  }

  return (
    <>
      <Row style={{ marginBottom: 16 }}>
        <Button danger onClick={() => setShowDelete(true)}>
          Delete addon
        </Button>
      </Row>
      <Row>
        <CreateUpdateAddonForm selectedAddonItem={selectedAddon} languageList={languageList} onSubmit={onSubmit} />
      </Row>
      <Modal
        title="Delete addon"
        open={showDelete}
        onOk={handleDelete}
        onCancel={() => setShowDelete(false)}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        Remove this addon? Linked products will no longer offer it.
      </Modal>
    </>
  );
}

export default AddonDetail;
